import { Add, Delete, EmojiPeople } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  TextField,
  Theme,
  useMediaQuery
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../../../../hooks';
// Components
import { Card, SMBSelect, Table } from '../../../../components';
import { addUserToPartner, deleteVendorUser } from '../../../../fetch';
import { formatInputPhoneNumber } from '../../../../helpers';
import {
  IPeople,
  IUserType,
  IVendorContact,
  IVendorDetail,
  userTypes,
  userTypesHashTable
} from '../../../../models';
import { AddEditExternalUser } from '../../manage-admin-users/ExternalUserModal';
import { MobileVendorUser } from './MobileVendorUser';
import { getCommonPeople } from '../../../../fetch/lookups';

interface IVendorUsers {
  currentVendor: IVendorDetail;
  vendorContacts: IVendorContact[];
  isLoadingVendorContacts: boolean;
  fetchVendorContacts: () => {};
}

export const VendorUsers: FC<IVendorUsers> = ({
  isLoadingVendorContacts,
  currentVendor,
  vendorContacts,
  fetchVendorContacts
}) => {
  // styles
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 600px)');
  // state
  const [isLoadingExternalUsers, setLoadingExternalUsers] = useState(false);
  const [isUserAutoOpen, setUserAutoOpen] = useState(false);
  const [isAddingUser, setAddingUser] = useState(false);
  const [externalUsers, setExternalUsers] = useState<IPeople[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<IPeople | string>('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [searchValueDebounced, setSearchValueDebounced] = useState('');

  const [isAddEditExternalUserModalOpen, setIsAddEditExternalUserModalOpen] =
    useState<boolean>(false);
  const [isRemoving, setIsRemoving] = useState(false);
  // context
  const { enqueueSnackbar } = useSnackbar();

  const fetchExternalUsers = async () => {
    setLoadingExternalUsers(true);
    try {
      const externalUsers = await getCommonPeople({
        query: searchValueDebounced,
        sortBy: 'FirstName',
        personType: 'VendorAffiliate'
      });
      setExternalUsers(externalUsers.records);
      if (externalUsers?.records?.length && !isUserAutoOpen) {
        setUserAutoOpen(true);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingExternalUsers(false);
    }
  };
  // effects
  useEffect(() => {
    if (searchValueDebounced) {
      fetchExternalUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValueDebounced]);
  // hooks
  useDebounce(
    () => {
      setSearchValueDebounced(inputValue);
    },
    500,
    [inputValue]
  );

  const handleRemove = async (original: IVendorContact) => {
    const result = window.confirm('Are you sure you want to remove this user?');
    if (result) {
      try {
        setIsRemoving(true);

        await deleteVendorUser(original.personId, currentVendor.vendorId);

        fetchVendorContacts();
        enqueueSnackbar('User removed from this Vendor!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `User was unable to be removed from this vendor.`, {
          variant: 'error'
        });
      } finally {
        setIsRemoving(false);
      }
    }
  };

  // columns
  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Email',
        accessor: 'email',
        sort: false
      },
      {
        Header: 'User Type',
        accessor: 'roles',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorContact } };
        }) => {
          return <>{original?.roles?.map(role => userTypesHashTable[role]).join(', ')}</>;
        }
      },
      {
        Header: 'Phone Number',
        accessor: 'phoneNumber',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorContact } };
        }) => {
          return <>{formatInputPhoneNumber(original.phoneNumber)}</>;
        }
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorContact } };
        }) => {
          return (
            <div className={classes.actionButtonTypes}>
              <Button
                color="inherit"
                startIcon={<Delete />}
                className={classes.deleteButton}
                onClick={async () => {
                  handleRemove(original);
                }}
              >
                Remove
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorContacts]);
  if (!vendorContacts) {
    return null;
  }

  const addUserToVendor = async (personId?: number, roles?: string[], shouldReload?: boolean) => {
    setAddingUser(true);
    try {
      const res = await addUserToPartner(
        currentVendor.vendorId,
        personId ?? (selectedPerson as IPeople).personId,
        {
          roles: roles ?? selectedRoles
        }
      );
      if (res?.Detail) {
        enqueueSnackbar(res.Detail, {
          variant: 'error'
        });
      } else if (res) {
        if (shouldReload) {
          fetchVendorContacts();
        }
        setSelectedRoles([]);
        setSelectedPerson('');
        setInputValue('');

        if (personId && roles.length > 0) {
          enqueueSnackbar(`User Created and Added!`, {
            variant: 'success'
          });
        } else {
          enqueueSnackbar(`User Added!`, {
            variant: 'success'
          });
        }
      } else {
        enqueueSnackbar('Error: Unable to add user, please try again', {
          variant: 'error'
        });
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Error: Unable to add user, please try again', {
        variant: 'error'
      });
    } finally {
      setAddingUser(false);
    }
  };

  return (
    <>
      <Card icon={<EmojiPeople />} title="Users">
        <Grid container className={classes.wrapper} spacing={1} alignItems="center">
          {currentVendor && (
            <>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Autocomplete
                    value={selectedPerson}
                    onInputChange={(e, value, reason) => {
                      if (reason === 'clear') {
                        setExternalUsers([]);
                      }
                      if (reason === 'reset') {
                        return;
                      }
                      setInputValue(value);
                    }}
                    // disable filter options so we see everything on typing
                    filterOptions={x => x}
                    onChange={(e, newValue: IPeople) => {
                      if (newValue && newValue?.firstName?.length) {
                        setSelectedPerson(newValue);
                      } else {
                        setSelectedPerson('');
                      }
                    }}
                    id="select-user"
                    options={externalUsers}
                    getOptionLabel={(option: IPeople) => {
                      return option ? `${option?.firstName} ${option.lastName}` : '';
                    }}
                    renderOption={(props, option: IPeople) => {
                      return (
                        <li {...props} key={option.personId}>
                          {option?.firstName} {option?.lastName}{' '}
                          {option?.email ? `(${option?.email})` : `()`}
                        </li>
                      );
                    }}
                    open={isUserAutoOpen}
                    onOpen={() => {
                      setUserAutoOpen(true);
                    }}
                    onClose={() => {
                      setUserAutoOpen(false);
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {isLoadingExternalUsers ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          )
                        }}
                        size="small"
                        name="name"
                        autoComplete="none"
                        label="Search for user"
                        variant="outlined"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <SMBSelect
                  variant="outlined"
                  onChange={e => {
                    setSelectedRoles(
                      typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : e.target.value
                    );
                  }}
                  SelectProps={{
                    multiple: true
                  }}
                  handleClear={() => setSelectedRoles([])}
                  name={`roles`}
                  id={`roles`}
                  label="User Type"
                  size="small"
                  value={selectedRoles}
                >
                  {userTypes.map((type: IUserType, index) => {
                    return (
                      <MenuItem key={`${index + 1}`} value={type.value}>
                        {type.name}
                      </MenuItem>
                    );
                  })}
                </SMBSelect>
              </Grid>
              <Grid item xs={12} md={2}>
                <LoadingButton
                  startIcon={<Add />}
                  loading={isAddingUser || isLoadingVendorContacts}
                  loadingIndicator="Adding..."
                  disabled={
                    isAddingUser ||
                    isLoadingVendorContacts ||
                    !(selectedPerson as IPeople).firstName ||
                    selectedRoles?.length === 0
                  }
                  onClick={() => addUserToVendor(undefined, undefined, true)}
                  variant="contained"
                  color="primary"
                >
                  Add User
                </LoadingButton>
              </Grid>
            </>
          )}
          <Grid
            item
            xs={12}
            md={3}
            sx={!currentVendor ? { textAlign: 'left' } : { textAlign: { xs: 'left', md: 'right' } }}
          >
            <Button
              startIcon={<Add />}
              onClick={() => {
                // 0 is internal, 1 is external
                setIsAddEditExternalUserModalOpen(true);
              }}
              variant="contained"
              color="primary"
            >
              New User
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Table
              columns={columns}
              data={vendorContacts || []}
              isLoading={isLoadingVendorContacts || isRemoving}
              hidePagination
              ResponsiveComponent={isMobile ? MobileVendorUser : undefined}
              mobileProps={{
                handleRemove: (val: any) => handleRemove(val)
              }}
            />
          </Grid>
        </Grid>
      </Card>
      <AddEditExternalUser
        open={isAddEditExternalUserModalOpen}
        onClose={() => {
          setIsAddEditExternalUserModalOpen(false);
        }}
        // Null, since it will always be adding new user.
        currentExternalUser={null}
        fetchExternalUsers={fetchVendorContacts}
        currentVendor={currentVendor}
        addUserToVendor={addUserToVendor}
        isVendorAffiliate
        modalTitle="Add Vendor Affiliate"
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {},
  actionButtonTypes: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
